import React from "react";

export function Logo({ light = false }) {
  return (
    <span className="inline-flex items-center gap-2.5" data-testid="brand-logo">
      <img src={light ? "/brand/p1-icon-dark.png" : "/brand/p1-icon.png"} alt="ParentOne Care" data-testid="brand-logo-icon"
        className="h-10 w-10 shrink-0 object-contain" />
      <span className="leading-none">
        <span className={`font-heading text-[22px] font-bold tracking-tight ${light ? "text-white" : "text-emerald-950"}`}>
          Parent<span className="text-terracotta">One</span>
        </span>
        <span className={`mt-1 block text-[10px] font-bold uppercase tracking-[0.28em] ${light ? "text-[#E9E4D8]" : "text-emerald-800"}`}>Care</span>
      </span>
    </span>
  );
}

export function Loading({ label = "Loading…" }) {
  return (
    <div className="flex min-h-[40vh] items-center justify-center" data-testid="loading-indicator">
      <div className="flex flex-col items-center gap-3">
        <div className="h-8 w-8 animate-spin rounded-full border-[3px] border-emerald-900 border-t-transparent" />
        <p className="text-sm text-stone-500">{label}</p>
      </div>
    </div>
  );
}

export function EmptyState({ icon: Icon, title, body, action }) {
  return (
    <div className="p1-card flex flex-col items-center gap-2 px-6 py-12 text-center" data-testid="empty-state">
      {Icon && <Icon className="h-8 w-8 text-stone-300" />}
      <p className="font-heading text-lg font-semibold text-stone-800">{title}</p>
      {body && <p className="max-w-sm text-sm text-stone-500">{body}</p>}
      {action}
    </div>
  );
}

export function StatusPill({ status }) {
  const map = {
    scheduled: "bg-sky-100 text-sky-800", upcoming: "bg-sky-100 text-sky-800",
    in_progress: "bg-amber-100 text-amber-800", requested: "bg-amber-100 text-amber-800",
    completed: "bg-emerald-100 text-emerald-800", paid: "bg-emerald-100 text-emerald-800",
    active: "bg-emerald-100 text-emerald-800", verified: "bg-emerald-100 text-emerald-800",
    success: "bg-emerald-100 text-emerald-800", resolved: "bg-emerald-100 text-emerald-800",
    assigned: "bg-blue-100 text-blue-800", confirmed: "bg-blue-100 text-blue-800",
    acknowledged: "bg-sky-100 text-sky-800", arranging: "bg-blue-100 text-blue-800",
    open: "bg-amber-100 text-amber-800", pending: "bg-amber-100 text-amber-800",
    in_progress_case: "bg-amber-100 text-amber-800", new: "bg-amber-100 text-amber-800",
    cancelled: "bg-stone-200 text-stone-600", closed: "bg-stone-200 text-stone-600",
    emergency: "bg-red-100 text-red-700", urgent: "bg-red-100 text-red-700",
    attention_required: "bg-amber-100 text-amber-800", routine: "bg-stone-100 text-stone-600",
    assessment_pending: "bg-amber-100 text-amber-800", onboarding: "bg-sky-100 text-sky-800",
    in_progress_status: "bg-amber-100 text-amber-800",
  };
  const label = String(status || "").replace(/_/g, " ");
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize ${map[status] || "bg-stone-100 text-stone-600"}`}
      data-testid={`status-pill-${status}`}>
      {label}
    </span>
  );
}

export function PageHeader({ title, sub, actions }) {
  return (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
      <div>
        <h1 className="font-heading text-2xl font-bold tracking-tight text-stone-900 sm:text-3xl">{title}</h1>
        {sub && <p className="mt-1 text-sm text-stone-500">{sub}</p>}
      </div>
      {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
    </div>
  );
}

export function StatCard({ icon: Icon, label, value, tone = "default", testid }) {
  const tones = {
    default: "bg-white text-stone-900", emerald: "bg-emerald-900 text-white",
    amber: "bg-amber-50 text-amber-900 border-amber-200", red: "bg-red-50 text-red-800 border-red-200",
  };
  return (
    <div className={`rounded-2xl border border-stone-200/90 p-4 shadow-sm ${tones[tone]}`} data-testid={testid}>
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider opacity-70">
        {Icon && <Icon className="h-4 w-4" />} {label}
      </div>
      <p className="mt-2 font-heading text-3xl font-bold">{value}</p>
    </div>
  );
}


export function ConfirmDelete({ title, body, onConfirm, onCancel, busy, testidPrefix = "confirm-delete" }) {
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-stone-900/60 p-4" data-testid={`${testidPrefix}-modal`}>
      <div className="w-full max-w-md rounded-3xl bg-white p-6 shadow-2xl">
        <p className="font-heading text-lg font-bold text-red-700" data-testid={`${testidPrefix}-title`}>{title}</p>
        <p className="mt-2 text-sm text-stone-600" data-testid={`${testidPrefix}-body`}>{body}</p>
        <div className="mt-5 flex gap-3">
          <button onClick={onCancel} disabled={busy} data-testid={`${testidPrefix}-cancel`}
            className="flex-1 rounded-full border border-stone-200 px-5 py-2.5 text-sm font-semibold text-stone-600 hover:bg-stone-50">Cancel</button>
          <button onClick={onConfirm} disabled={busy} data-testid={`${testidPrefix}-confirm`}
            className="flex-1 rounded-full bg-red-600 px-5 py-2.5 text-sm font-bold text-white hover:bg-red-700 disabled:opacity-50">{busy ? "Deleting…" : "Delete Permanently"}</button>
        </div>
      </div>
    </div>
  );
}